'use client'

import { useEffect, useState } from 'react'

/**
 * Types a line, holds it, erases it like backspace, then types the next one.
 * All lines are rendered invisibly underneath so the space never jumps.
 */
export function Typewriter({
  lines,
  className = '',
  typeMs = 55,
  deleteMs = 28,
  holdMs = 1900,
  textClassName = 'ncp-gradient-text',
}: {
  lines: string[]
  className?: string
  typeMs?: number
  deleteMs?: number
  holdMs?: number
  textClassName?: string
}) {
  const [i, setI] = useState(0)
  const [text, setText] = useState(lines[0] ?? '')
  const [phase, setPhase] = useState<'hold' | 'delete' | 'type'>('hold')

  useEffect(() => {
    if (lines.length < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const id = window.setInterval(() => setI((n) => (n + 1) % lines.length), 3200)
      return () => window.clearInterval(id)
    }
    let id = 0
    const full = lines[i]
    if (phase === 'hold') id = window.setTimeout(() => setPhase('delete'), holdMs)
    else if (phase === 'delete') {
      if (text.length === 0) {
        id = window.setTimeout(() => {
          setI((n) => (n + 1) % lines.length)
          setPhase('type')
        }, 280)
      } else id = window.setTimeout(() => setText((s) => Array.from(s).slice(0, -1).join('')), deleteMs)
    } else {
      if (text === full) setPhase('hold')
      else id = window.setTimeout(() => setText(Array.from(full).slice(0, Array.from(text).length + 1).join('')), typeMs + Math.random() * 40)
    }
    return () => window.clearTimeout(id)
  }, [phase, text, i, lines, typeMs, deleteMs, holdMs])

  // reduced-motion fallback swaps whole lines
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setText(lines[i] ?? '')
  }, [i, lines])

  return (
    <span className={`relative inline-grid ${className}`}>
      {lines.map((l) => (
        <span key={l} aria-hidden className="invisible col-start-1 row-start-1">
          {l}
          <span className="ncp-caret" />
        </span>
      ))}
      <span className="sr-only">{lines.join(' · ')}</span>
      <span className="col-start-1 row-start-1" aria-hidden>
        <span className={textClassName}>{text}</span>
        <span className="ncp-caret" aria-hidden />
      </span>
    </span>
  )
}
